import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  RelationshipDropdownValues,
  RelationshipTypeValues,
  RelationshipValues
} from '../relatedOrganisations/RelatedOrganisations.types'
import { RelationshipBadge } from '../relationshipBadge/RelationshipBadge'

import { Dialog, Loader } from '@percent/admin-dashboard/common/components'
import { useGetDgrHelpers, useMutation, useQueryList } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ApproveModalProps } from './Dialog.types'
import styles from './Dialog.module.scss'
import { Badge, Box, Button, FormField, Spacer, Typography } from '@percent/lemonade'
import { ActionsDialogAction } from '@percent/admin-dashboard/common/components/dialog/actionDialog/ActionDialog'
import { renderAddressString } from '@percent/admin-dashboard/common/utility/renderAddressString'
import { filterMutuallyExclusiveOrganisationTypes } from '@percent/admin-dashboard/app/validations/nonprofitDetail/utils/filterMutuallyExclusiveOrganisationTypes'
import { OrganisationTypes } from '@percent/domain/verifications'
import { SimilarOrganisationActionValues } from '@percent/admin-dashboard/app/validations/nonprofitDetail/similarOrganisations/SimilarOrganisations.types'
import { DecoratedActivitySubTag } from '@percent/admin-dashboard/common/hooks/useActivityTags/useActivityTags.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { DgrStatus } from '@percent/admin-dashboard/api/types'
import { CountrySpecificData } from './registryModal/RegistryModal.types'

export const createRelationshipType = (relationshipType?: '' | RelationshipDropdownValues) => {
  switch (relationshipType) {
    case RelationshipDropdownValues.MERGE:
      return RelationshipTypeValues.MERGE
    case RelationshipDropdownValues.FISCAL_SPONSORSHIP_CHILD:
    case RelationshipDropdownValues.FISCAL_SPONSORSHIP_PARENT:
      return RelationshipTypeValues.FISCAL_SPONSORSHIP
    default:
      return RelationshipTypeValues.BRANCH
  }
}

export function ApproveModal({
  isOpened,
  setIsOpened,
  finishAction,
  validationRequestId,
  validationRegistryId,
  nonprofitName,
  validationCountryCode,
  registryId,
  organisationTypes,
  validationOrganisationTypes,
  relationshipType,
  relationshipOrganisation,
  registryInformationNotFound,
  duplicateAction,
  duplicateOrganisation,
  subTags,
  selectedSubTags,
  insufficientSubTagsInformation,
  noValidSubtags,
  countrySpecificData
}: ApproveModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const registriesSupportedTypesFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_REGISTRIES_SUPPORTED_TYPES)
  const { mapCountrySpecificDataToDgr } = useGetDgrHelpers()

  const [{ errorMessage, isLoading, success }, { apiFunc }] = useMutation(
    adminService.commands.acceptValidation,
    finishAction
  )
  const isNonprofitOrganisation = organisationTypes.includes(OrganisationTypes.Nonprofit)

  const isIndianValidationRequest = validationCountryCode === 'IND'
  const isIndianNonprofit = isIndianValidationRequest && isNonprofitOrganisation

  const isAustralianValidationRequest = validationCountryCode === 'AUS'

  const queryParams = {
    countryCode: validationCountryCode,
    pageSize: undefined,
    supportedTypes: registriesSupportedTypesFeatureFlag
      ? filterMutuallyExclusiveOrganisationTypes(organisationTypes, [])
      : undefined
  }

  const [{ dataOrNull }, { query }] = useQueryList(adminService.getRegistries, queryParams)

  useEffect(() => {
    if (registriesSupportedTypesFeatureFlag) {
      query(queryParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registriesSupportedTypesFeatureFlag])

  const isMergeRelationship = relationshipType === RelationshipDropdownValues.MERGE

  const isChildRelation =
    relationshipType &&
    [RelationshipDropdownValues.BRANCH_CHILD, RelationshipDropdownValues.FISCAL_SPONSORSHIP_CHILD].includes(
      relationshipType
    )

  const relationshipValue = isChildRelation ? RelationshipValues.CHILD : RelationshipValues.PARENT

  const concatSubmitedOrganisationTypes = () =>
    filterMutuallyExclusiveOrganisationTypes(organisationTypes, [
      ...(duplicateOrganisation?.organisationTypes ?? []),
      ...(relationshipOrganisation?.result?.organisationTypes ?? [])
    ])

  const submittedOrganisationTypes = concatSubmitedOrganisationTypes()

  const relationshipData = {
    relationships: [
      {
        organisationId: relationshipOrganisation?.result.id,
        type: createRelationshipType(relationshipType),
        ...(!isMergeRelationship ? { relationship: relationshipValue } : {})
      }
    ]
  }

  const registry = dataOrNull?.find(({ id }) => registryId === id)

  const duplicateDisplayRegistryName =
    duplicateAction === SimilarOrganisationActionValues.MERGE
      ? dataOrNull?.find(({ id }) => duplicateOrganisation?.registry === id)?.englishName ||
        dataOrNull?.find(({ id }) => duplicateOrganisation?.registry === id)?.name
      : t('dialog.similarOrganisations.systemWillGenerateRegistry')

  const displayRegistry = registryInformationNotFound
    ? duplicateDisplayRegistryName
    : registry?.englishName || registry?.name

  const duplicateOrganisationRegistryIdText =
    duplicateAction === SimilarOrganisationActionValues.MERGE
      ? duplicateOrganisation?.registryId
      : t('dialog.similarOrganisations.systemWillGenerateRegistryId')

  const organisationRegistryId = registryInformationNotFound
    ? duplicateOrganisationRegistryIdText
    : validationRegistryId

  const duplicateOrganisationRegistryId =
    duplicateAction === SimilarOrganisationActionValues.MERGE ? duplicateOrganisation?.registryId : undefined

  const duplicateRegistryId =
    duplicateAction === SimilarOrganisationActionValues.MERGE ? duplicateOrganisation?.registry : undefined

  const submittedSubTags =
    subTags?.map(subTag => ({
      id: subTag.id,
      meetsCriteria: Boolean(selectedSubTags?.includes(subTag.id))
    })) || []

  const formatCountrySpecificData = (data?: CountrySpecificData) => {
    if (!data) {
      return {}
    }

    const isAustralianOrganization = validationCountryCode === 'AUS'
    const countrySpecificDataPayload: { fcra?: string | null; dgrStatusList?: DgrStatus[] | null } = {}

    if (isIndianNonprofit && data.fcra) {
      countrySpecificDataPayload.fcra = data.fcra
    }

    if (isAustralianOrganization && data.dgrStatusList?.length) {
      countrySpecificDataPayload.dgrStatusList = data.dgrStatusList
    }

    if (Object.keys(countrySpecificDataPayload).length > 0) {
      return { countrySpecificData: countrySpecificDataPayload }
    }

    return {}
  }
  const formatSubTagsSubmission = () => {
    if (noValidSubtags) {
      return {
        eligibilitySubscription: {
          subTags: subTags?.map(subTag => ({
            id: subTag.id,
            meetsCriteria: false
          }))
        }
      }
    }

    if (insufficientSubTagsInformation) {
      return { eligibilitySubscription: { subTags: null } }
    }

    return submittedSubTags.length ? { eligibilitySubscription: { subTags: submittedSubTags } } : {}
  }

  const handleSubmit = () => {
    apiFunc({
      payload: {
        registryId: registryInformationNotFound ? duplicateRegistryId : registryId,
        organisationRegistryId: registryInformationNotFound ? duplicateOrganisationRegistryId : validationRegistryId,
        organisationTypes: submittedOrganisationTypes,
        ...formatSubTagsSubmission(),
        ...(relationshipType ? relationshipData : {}),
        ...(duplicateAction === SimilarOrganisationActionValues.MERGE
          ? {
              relationships: [
                {
                  organisationId: duplicateOrganisation?.id,
                  type: SimilarOrganisationActionValues.MERGE
                }
              ]
            }
          : {}),
        ...formatCountrySpecificData(countrySpecificData)
      },
      validationRequestId
    })
  }

  const disableConfirmButton = !registryInformationNotFound && (!registryId || !validationRegistryId)

  const partnerProgramMessageId = `${t('dialog.eligibleForPartnerProgram')} ${(
    validationOrganisationTypes || submittedOrganisationTypes
  )
    ?.map(organisationType => t(`organisationTypes.${organisationType}`))
    .join(' & ')
    .toLowerCase()} ${t('dialog.eligibleForPartnerProgramEnding')}`

  const successMessage = partnerProgramMessageId

  const canShowRelatedOrganisation =
    isMergeRelationship ||
    (relationshipType &&
      [RelationshipDropdownValues.BRANCH_CHILD, RelationshipDropdownValues.FISCAL_SPONSORSHIP_CHILD].includes(
        relationshipType
      ))

  const isOnlyEducationOrganisation =
    organisationTypes?.length === 1 && organisationTypes.includes(OrganisationTypes.Education)

  const relationTypeText = t(`dialog.relatedOrganisations.${isMergeRelationship ? 'merge' : 'parent'}`)

  return (
    <Dialog
      openModal={isOpened}
      onClose={() => setIsOpened(false)}
      withoutHeader={success || !!errorMessage}
      headerTitle={t('dialog.confirm.title')}
    >
      {isLoading ? (
        <Loader />
      ) : errorMessage ? (
        <ActionsDialogAction
          handleOnClose={() => setIsOpened(false)}
          description={errorMessage}
          title={t('dialog.somethingWentWrong')}
          buttonTitle={t('button.done')}
          successIcon={false}
        />
      ) : success ? (
        <ActionsDialogAction
          handleOnClose={() => setIsOpened(false)}
          description={`${nonprofitName} ${successMessage}`}
          title={t('dialog.hasBeenApproved')}
          buttonTitle={t('button.done')}
          successIcon
        />
      ) : (
        <>
          <Box className={styles.modalBody}>
            <Typography variant="large">
              {t('dialog.confirm.description')} <span style={{ fontFamily: 'MarkProMedium' }}>{nonprofitName}: </span>
            </Typography>
          </Box>

          <Box className={styles.form}>
            <FormField label={t('dialog.approveValidationRequest.organisationType')} data-testid="organisationType">
              <div>
                {submittedOrganisationTypes?.map((organisationType: OrganisationTypes) => (
                  <>
                    <Badge variant="informative" icon="categories">
                      {t(`organisationTypes.${organisationType}`)}
                    </Badge>
                    <Spacer size={4} />
                  </>
                ))}
              </div>
            </FormField>
          </Box>
          {isOnlyEducationOrganisation && (
            <Box className={styles.form}>
              <FormField label={t('table.eligibilityTags')} data-testid="eligibilityTags">
                <div>
                  {insufficientSubTagsInformation && (
                    <Typography variant="small">
                      {t('dialog.submitTags.flagOrganisation')}
                      <span style={{ fontFamily: 'MarkProMedium' }}> {nonprofitName}.</span>
                    </Typography>
                  )}
                  {noValidSubtags && <Typography variant="small">{t('typography.noValidSubtags')}</Typography>}
                  {!!selectedSubTags?.length &&
                    selectedSubTags?.map(selectedSubTagId => (
                      <>
                        <Badge variant="default" icon="categories">
                          {
                            (subTags as Array<DecoratedActivitySubTag>)?.find(
                              ({ id: subTagId }) => selectedSubTagId === subTagId
                            )?.name
                          }
                        </Badge>
                        <Spacer size={4} />
                      </>
                    ))}
                </div>
              </FormField>
            </Box>
          )}
          <Box className={styles.form}>
            <FormField label={t('table.registry')} data-testid="registryName">
              <Typography variant="small">{displayRegistry}</Typography>
            </FormField>
          </Box>
          <Box className={styles.form}>
            <FormField label={t('typography.registryId')} data-testid="registryId">
              <Typography variant="small">{organisationRegistryId}</Typography>
            </FormField>
          </Box>
          {isIndianNonprofit && (
            <Box className={styles.form}>
              <FormField label={t('dialog.approveValidationRequest.fcraId')} data-testid="registryId">
                <Typography variant="small">
                  {countrySpecificData?.fcra ? countrySpecificData?.fcra : t('typography.notProvided')}
                </Typography>
              </FormField>
            </Box>
          )}

          {isAustralianValidationRequest && (
            <Box className={styles.form}>
              <FormField label={t('dialog.approveValidationRequest.dgrStatus')} data-testid="dgrStatus">
                <Typography variant="small">
                  {mapCountrySpecificDataToDgr(countrySpecificData) ?? t('typography.notProvided')}
                </Typography>
              </FormField>
            </Box>
          )}

          {relationshipType && (
            <Box className={styles.form}>
              <FormField label={t('dialog.relatedOrganisations.label')} data-testid="registryId">
                <>
                  <span className={styles.organisationRelationship}>
                    <Typography variant="small">{t('dialog.relatedOrganisations.markedAs')}</Typography>{' '}
                    <RelationshipBadge relationshipType={relationshipType} />
                  </span>
                  {canShowRelatedOrganisation && (
                    <Typography variant="small" className={styles.relationshipType}>
                      <span>{relationTypeText}</span>
                      {relationshipOrganisation ? (
                        <span className={styles.organisationName}>
                          {relationshipOrganisation?.result.name}
                          {relationshipOrganisation?.result.address && (
                            <div>{renderAddressString(relationshipOrganisation?.result.address)}</div>
                          )}
                        </span>
                      ) : (
                        <span className={styles.organisationName}>
                          {t('form.relatedOrganisations.parentNotInDatabase')}
                        </span>
                      )}
                    </Typography>
                  )}
                </>
              </FormField>
            </Box>
          )}
          {registryInformationNotFound && duplicateAction === SimilarOrganisationActionValues.MERGE && (
            <Box className={styles.form}>
              <FormField label={t('dialog.similarOrganisations.label')} data-testid="confirmSimilarOrganisation">
                <Typography variant="small">
                  <span className={styles.organisationName}>
                    {duplicateOrganisation?.name}
                    {duplicateOrganisation?.address && <div>{renderAddressString(duplicateOrganisation?.address)}</div>}
                  </span>
                </Typography>
              </FormField>
            </Box>
          )}
          <Box className={styles.modalBody}>
            <Typography variant="large">{t('dialog.confirm.footer')}</Typography>
          </Box>
          <Box className={styles.dialogFooter}>
            <Button
              disabled={disableConfirmButton}
              type="submit"
              variant="primary"
              data-testid="btn-confirm-validation"
              loading={isLoading}
              onClick={handleSubmit}
            >
              {t('button.confirmInformation')}
            </Button>
            <Spacer size={4} />
            <Button
              variant="secondary"
              data-testid="btn-cancel-validiation"
              onClick={() => {
                setIsOpened(false)
              }}
            >
              {t('button.cancel')}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  )
}
